import lzwCompress from 'lzwcompress'
import { MIDDLE_C, TICKS_PER_LOOP } from '../../constants'
import { isValidComposition } from '../../utils'
import { compressSnapshot, decompressSnapshot } from './CompressionUtils'
import { CURRENT_SONG_VERSION } from './SongSerializationUtils'
import {
  stemMigration2to3,
  stemMigration3to4,
  stemMigration4to5,
  stemMigration5to6
} from './StemMigrations'

export const DRAFT_CONVERSION_NOTE_SCALE = 3

/*
 * Processes the given stem or song snapshot for storage in a database or localstorage.
 *
 * Note that stem versions are always kept in line with song versions.
 *
 * Latest Version:
 * {
 *  version: 5,
 *  compressedSnapshot: (gzip compressed stringified snapshot, 8 bit number array)
 * }
 *
 * Previous Versions:
 * {
 *  version: 2-4,
 *  compressedSnapshot: (lzw compressed stringified snapshot, 12 bit number array)
 * }
 */
export const serializeStemSnapshot = (snapshot: any) => {
  return {
    version: CURRENT_SONG_VERSION,
    compressedSnapshot: compressSnapshot(snapshot)
  }
}

/** Deserializes and migrates the given stem, returning a snapshot of the stem with the latest version. */
export const deserializeStemSnapshot = (serializedStem: any) => {
  // Some stems don't have arpeggi composition and don't need to be deserialized.
  if (!isValidComposition(serializedStem)) {
    return null
  }
  // First, extract the snapshot from the serialized song.
  let parsedStem = typeof serializedStem === 'string' ? JSON.parse(serializedStem) : serializedStem

  // Then check the version, and manually apply correct migration and decompression.
  if (!parsedStem.version || parsedStem.version < 3) {
    parsedStem = stemMigration2to3(parsedStem)
  }

  const version = parsedStem.version
  if (parsedStem.compressedSnapshot) {
    if (version <= 4) {
      parsedStem = {
        ...lzwCompress.unpack(parsedStem.compressedSnapshot),
        version: parsedStem.version
      }
    } else if (parsedStem.version <= 6) {
      parsedStem = decompressSnapshot(parsedStem.compressedSnapshot)
    }
  }

  if (version < 4) {
    parsedStem = stemMigration3to4(parsedStem)
  }
  if (version < 5) {
    parsedStem = stemMigration4to5(parsedStem)
  }
  if (version < 6) {
    parsedStem = stemMigration5to6(parsedStem)
  }
  return parsedStem
}

interface Note {
  start: number
  duration: number
  note: number
}

/**
 * Populates a note collection snapshot from an array of booleans, where the array represents an entire loop
 * this populates every note with the value MIDDLE_C -- useful for converting drum noterows to note collections
 */
export const booleanArrayToNoteCollection = (arr: boolean[]): { notes: Note[] } => {
  const notes: Note[] = []
  if (arr === null) return { notes }
  const beatWidthTicks = TICKS_PER_LOOP / arr.length
  arr.forEach((note, index) => {
    if (!note) return
    notes.push({
      start: index * (TICKS_PER_LOOP / arr.length),
      duration: beatWidthTicks,
      note: MIDDLE_C
    })
  })
  return { notes }
}

/**
 * Populates a note collection snapshot from an array of arrays of booleans, just like how we used
 * to represent NoteRowStore -- useful for converting melodic noterows to note collections
 */
export const booleanArray2DToNoteCollection = (
  arr2D: boolean[][],
  durationTicks?: number,
  isAutorelease?: boolean
): { notes: Note[] } => {
  const notes: Note[] = []
  if (arr2D === null) return { notes }

  arr2D.forEach((arr, noteValue) => {
    if (arr === null) return

    const beatWidthTicks =
      durationTicks || (TICKS_PER_LOOP / arr.length) * DRAFT_CONVERSION_NOTE_SCALE
    arr.forEach((note, index) => {
      if (!note) return

      const nextTrueIndex = arr.slice(index).indexOf(true)
      const duration =
        isAutorelease && index !== arr.length - 1
          ? (nextTrueIndex === -1 ? arr.length - 1 : nextTrueIndex) * TICKS_PER_LOOP - 1
          : beatWidthTicks
      notes.push({
        start: index * (TICKS_PER_LOOP / arr.length),
        note: noteValue,
        duration
      })
    })
  })
  return { notes }
}
